import React, { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import cx from "classnames";
import dayjs from "dayjs";
import { Icon } from "@sunwing/icons/westjet-vacations";
import { Button, Notifications } from "@sunwing/wvi-components";
import { useNotificationLabels } from "../../context/Notifications/useNotificationsLabels";
import { LinkConnector, INTERNAL_URL_REGEX } from "../LinkConnector";
import { defaultLabels } from "../../context/Notifications/constants";
import { ALERTS_REDIRECT_MAP } from "../../const/const";

import * as styles from "./Notifications.module.scss";

const NotificationsConnector = ({
	notificationsLiveData = [],
	notificationsStaticData = [],
	locale = "en-CA",
}) => {
	// Manage localizations
	const providedLabels = useNotificationLabels();

	const labels = { ...defaultLabels, ...providedLabels };
	const [notifications, setNotifications] = useState([]);
	const triggerRef = useRef(null);
	const [activeAlerts, setActiveAlerts] = useState();
	const language = locale?.substring(0, 2) ?? "en";
	const currentYear = dayjs().year();

	const filters = [
		{
			filterId: "all",
			filterName: labels.menuHeadingAll,
			filterIcon: "home",
			filterHeading: labels.menuTitleAll,
			filterLinkText: "",
			filterLink: "",
			noResults: labels.noResultsAll,
		},
		{
			filterId: "alert",
			filterName: labels.menuHeadingAlert,
			filterIcon: "warning",
			filterHeading: labels.menuTitleAlert,
			filterLinkText: labels.menuViewAlert,
			filterLink: `${language === "fr" ? "https://www.westjet.com/fr-ca/soyez-pret-a-voyager/avis-aux-voyageurs" : "https://www.westjet.com/en-ca/get-travel-ready/advisories"}`,
			noResults: labels.noResultsAlert,
		},
		{
			filterId: "offer",
			filterName: labels.menuHeadingOffer,
			filterIcon: "deal",
			filterHeading: labels.menuTitleOffer,
			filterLinkText: labels.menuViewOffer,
			filterLink: `/${language}/${language === "fr" ? "aubaines" : "deals"}`,
			noResults: labels.noResultsOffer,
		},
	];

	useEffect(() => {
		const staticNotification = notificationsStaticData
			?.map(notification => {
				let entryUrl,
					activeYear,
					title,
					date = notification.node.updatedAt,
					type = "",
					heading = "",
					message = "";

				const entry = notification.node.entry;
				const entryId = entry?.sys?.contentType?.sys?.id;

				// Generate URLs
				if (entryId === "hotelChain" || entryId === "destination" || entryId === "page") {
					type = "offer";
					title = notification.node?.heading ?? labels.menuHeadingOffer;
					heading = notification.node?.subheading ?? heading;

					if (entryId === "hotelChain") {
						entryUrl = `/${language}/hotels/hotel-brands/${entry.slug}`;
					} else if (entryId === "destination") {
						entryUrl = `/${language}/destinations/${entry.slug}`;
					} else if (entryId === "page") {
						entryUrl = `/${language}${entry.slug.startsWith("/") ? entry.slug : `/${entry.slug}`}`;
					}
				}

				if (entryId === "travelAdvisory") {
					// Don't adjust for localtime
					date = dayjs(entry.activeDate).utc(false).format("YYYY-MM-DD");
					activeYear = dayjs(entry.activeDate).utc(false).year();
					type = "alert";
					title = notification.node?.heading ?? labels.menuHeadingAlert;
					message = JSON.parse(entry?.content?.raw).content?.[0]?.content?.[0]?.value;
					entryUrl = `/${ALERTS_REDIRECT_MAP[`${language}-CA`]}${activeYear < currentYear ? `/${activeYear}` : ""}#${entry.contentful_id}`;
				}

				return {
					type,
					title: notification.node.heading ?? title,
					heading: notification.node.subheading ?? heading,
					message: notification.node?.message?.raw
						? JSON.parse(notification.node.message.raw).content?.[0]?.content?.[0]?.value
						: message,
					date: date,
					url: notification.node?.url ? notification.node.url : entryUrl,
					urlTarget: "_self",
					id: notification.node.contentful_id,
				};
			})
			.sort((a, b) => new Date(b.date) - new Date(a.date));

		setNotifications(staticNotification);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (notificationsLiveData?.length) {
			setNotifications(notificationsLiveData);
		}
	}, [notificationsLiveData]);

	if (filters?.length < 1) return null;
	return (
		<>
			<Button
				aria-haspopup="dialog"
				aria-controls="notificationsPopover"
				aria-label={labels.a11yToggleNotifications?.replace("{{ active }}", activeAlerts)}
				ref={triggerRef}
				defaultStyle
				className={cx(styles.notifications, activeAlerts && styles.offset)}>
				<span
					className={cx(styles.notificationsWrapper, {
						[styles.notificationsWrapperActive]: activeAlerts >= 1,
					})}>
					{activeAlerts >= 1 && <span className={styles.notificationsCount}>{activeAlerts}</span>}
					<Icon name="notification" className={styles.notificationsIcon} hidden={true} />
				</span>
			</Button>

			<Notifications
				labels={{
					a11yOpenLiveRegion: labels.a11yOpenLiveRegion,
					a11yClosedLiveRegion: labels.a11yClosedLiveRegion,
					a11yCloseMenu: labels.a11yCloseMenu,
					a11yList: labels.a11yList,
					a11yMenu: labels.a11yMenu,
				}}
				storageId="notifications-viewed"
				locale={locale}
				data={notifications}
				menuSettings={filters}
				triggerElement={triggerRef}
				onGetActiveTotal={active => {
					setActiveAlerts(active);
				}}
				className={styles.notificationWrapper}
				renderMenuIcon={iconName => <Icon name={iconName} hidden />}
				renderCloseIcon={iconName => <Icon name={iconName} hidden />}
				renderFilterLink={(activeFilter, className) => (
					<LinkConnector
						to={activeFilter.filterLink}
						target={!INTERNAL_URL_REGEX.test(activeFilter.filterLink) ? "_blank" : "_self"}
						className={className}
						showExternalIcon={false}
						icon="chevron-right-micro">
						{activeFilter.filterLinkText}
					</LinkConnector>
				)}
				renderNotificationIcon={iconName => <Icon name={iconName} hidden />}
				renderNotificationLink={(href, childrenItems, className, target) => (
					<LinkConnector
						to={href}
						target={target}
						className={className}
						showExternalIcon={false}
						noStyle>
						{childrenItems}
					</LinkConnector>
				)}
				classNames={{
					listItemHeading: styles.listItemHeading,
					listItemIcon: styles.listItemIcon,
				}}
			/>
		</>
	);
};

NotificationsConnector.propTypes = {
	notificationsLiveData: PropTypes.array,
	notificationsStaticData: PropTypes.array,
	locale: PropTypes.oneOf(["en-CA", "fr-CA"]),
};

export { NotificationsConnector };
